import { useState, useEffect } from 'react';
import { useClubContent } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';

// Default club info (fallback)
const defaultClubInfo = {
  mission: 'To develop young football talent in Trinidad & Tobago through world-class coaching, competitive play, and character building.',
  vision: 'To be the premier youth football club in the Caribbean, producing champions on and off the field.',
  values: 'Excellence, Integrity, Teamwork, Respect, and Passion',
  phone: '+1 (868) 623-8555',
  email: 'info@example.com',
  address: 'Training Ground, Port of Spain, Trinidad & Tobago',
  hours: 'Mon - Fri: 9 AM - 6 PM, Sat: 8 AM - 4 PM, Sun: Closed'
};

function Contact() {
  const { club } = useClub();
  
  // Use tenant-scoped hook for club info
  const { content: clubInfoContent } = useClubContent('clubInfo');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [clubInfo, setClubInfo] = useState(defaultClubInfo);

  // Sync club info from hook
  useEffect(() => {
    if (clubInfoContent) {
      setClubInfo({ ...defaultClubInfo, ...clubInfoContent });
    }
  }, [clubInfoContent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTimeout(() => {
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    }, 500);
  };

  // Build contact info from clubInfo state
  const contactInfo = [
    { id: 1, title: "Phone", icon: "📞", content: clubInfo.phone || "+1 (868) 623-8555", desc: "Call us during business hours" },
    { id: 2, title: "Email", icon: "📧", content: clubInfo.email || "info@clublinktt.com", desc: "We'll respond within 24 hours" },
    { id: 3, title: "Location", icon: "📍", content: clubInfo.address?.split(',')[0] || "ClubLink TT Training Ground", desc: clubInfo.address?.split(',').slice(1).join(',').trim() || "Port of Spain, Trinidad & Tobago" },
    { id: 4, title: "Hours", icon: "🕐", content: clubInfo.hours?.split(',')[0] || "Mon - Fri: 9 AM - 6 PM", desc: clubInfo.hours?.split(',').slice(1).join(',').trim() || "Sat: 8 AM - 4 PM | Sun: Closed" }
  ];

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <h1>CONTACT US</h1>
        <p>Have questions? We'd love to hear from you. Get in touch with {club?.name || 'us'} today.</p>
      </section>

      <section className="contact-info-section">
        <div className="contact-info-grid">
          {contactInfo.map(info => (
            <div key={info.id} className="contact-info-card">
              <span className="info-icon">{info.icon}</span>
              <h3 className="info-title">{info.title}</h3>
              <p className="info-content">{info.content}</p>
              <p className="info-description">{info.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="contact-form-section">
        <div className="form-container">
          <div className="form-header">
            <h2>SEND US A MESSAGE</h2>
            <p>Fill out the form below and we'll get back to you as soon as possible.</p>
          </div>

          {submitted && <div className="success-message">✅ Thank you! We'll be in touch shortly.</div>}

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your full name" />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Enter your email" />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required>
                <option value="">Select a subject</option>
                <option value="registration">Player Registration</option>
                <option value="coaching">Coaching Opportunities</option>
                <option value="tournaments">Tournaments & Events</option>
                <option value="kit">Kit Shop & Merchandise</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us what's on your mind..." rows="6"></textarea>
            </div>

            <button type="submit" className="btn-submit">SEND MESSAGE →</button>
          </form>
        </div>
      </section>

      <section className="club-info-section">
        <h2>ABOUT {club?.name?.toUpperCase() || 'US'}</h2>
        <div className="club-info-grid">
          <div className="club-info-card">
            <h3>Our Mission</h3>
            <p>{clubInfo.mission}</p>
          </div>
          <div className="club-info-card">
            <h3>Our Vision</h3>
            <p>{clubInfo.vision}</p>
          </div>
          <div className="club-info-card">
            <h3>Our Values</h3>
            <p>{clubInfo.values}. These values guide everything we do.</p>
          </div>
        </div>
      </section>

      <section className="social-section">
        <h2>FOLLOW US</h2>
        <p>Stay updated on news, events, and highlights from {club?.name || 'us'}</p>
        <div className="social-links">
          <a href="#facebook" className="social-btn"><span className="social-icon">f</span><span>Facebook</span></a>
          <a href="#instagram" className="social-btn"><span className="social-icon">📸</span><span>Instagram</span></a>
          <a href="#twitter" className="social-btn"><span className="social-icon">𝕏</span><span>Twitter</span></a>
          <a href="#youtube" className="social-btn"><span className="social-icon">▶</span><span>YouTube</span></a>
        </div>
      </section>
    </div>
  );
}

export default Contact;
